import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Spinner from '@components/molecules/Spinner';
import CommonRadioButton from '@components/molecules/radioButton/RadioButton';
import CommonCheckBox from '@src/components/molecules/checkBox/CheckBox';
import { CallPostOptionGroup, CallGetOptionGroup, CallPutOptGroup } from '@src/library/request/callProduct';
import { useQueryClient } from '@tanstack/react-query';
import { PRODUCT_KEYS } from '@src/types/productType';
import { useMenuPermission } from '@src/recoil/permission';

let prevItem: any = {
  require: null,
  maxSelect: '',
  minSelect: '',
};

const OptionGroupAdd = () => {
  const [searchParams] = useSearchParams();

  const storeId = searchParams.get('storeID');
  const optionGroupID = searchParams.get('optionGroupID');
  const selectType = searchParams.get('selectType');
  const options = searchParams.get('options');
  const queryClient = useQueryClient();

  const { isNoAuth1, isNoAuth5, isNoAuth10 } = useMenuPermission(storeId);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [requiredRadio, setRequiredRadio] = useState(null);
  const [isDirty, setIsDirty] = useState(false); // 수정 여부

  const [item, setItem] = useState({
    children: [
      {
        isDisplay: 1,
        maxSelect: 0,
        name: '',
        price: '',
        alcohol: 0,
      },
    ],
    isDisplay: 1,
    isRequired: 0,
    maxSelect: '',
    minSelect: '',
    selectType: null,
    title: '',
  });

  const isEdit = !!optionGroupID;
  const permissionDisabled = useMemo(() => {
    // 추가 모드: isNoAuth1
    if (!isEdit) return isNoAuth1;

    // 수정 모드 판단
    if (selectType) return isNoAuth5; // 그룹명 수정
    if (options) return isNoAuth10; // 옵션명/가격 수정

    return isNoAuth1; // 기본
  }, [isEdit, selectType, options, isNoAuth1, isNoAuth5, isNoAuth10]);

  const handleBtnDisabled = useMemo(() => {
    if (step === 1) {
      return item.title.length <= 0;
    }
    if (step === 2) {
      return !(
        requiredRadio !== null &&
        (Number(item.minSelect) <= Number(item.maxSelect) || (!item.minSelect && !item.maxSelect))
      );
    }
    if (step === 3) {
      return item.children.some((v) => v.name === '') || item.children.length === 0;
    }
    if (step === 4) {
      return false;
    }

    return true;
  }, [item, step, requiredRadio]);

  const goPrevStep = () => {
    setStep(step - 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true);
    let { name, value } = e.target;

    if (e.target.name === 'title') {
      value = value.slice(0, 255);
    }

    if (requiredRadio === 1 && value === '0') {
      return;
    }
    if (name === 'minSelect' || name === 'maxSelect') {
      if (/\D/.test(value)) {
        return;
      }
    }
    setItem((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const postAddOtpGroup = async () => {
    setLoading(true);

    const param = {
      options: [
        {
          ...item,
          isRequired: requiredRadio,
          minSelect: item.minSelect || (requiredRadio ? 1 : 0),
          maxSelect: item.maxSelect || (requiredRadio ? 1 : 0),
          children: requiredRadio
            ? item.children.map((v) => {
                return { ...v, maxSelect: 0 };
              })
            : item.children,
        },
      ],
    };

    try {
      const res = await CallPostOptionGroup(storeId, param);
      if (res.result.code === 1) {
        window.alert('옵션 그룹이 추가되었습니다.');
        queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
        window.history.back();
      } else {
        window.alert(res.result.message);
      }
    } catch (error) {
      console.log('error', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const putOtpGroup = async () => {
    setLoading(true);

    const param = {
      options: [
        {
          optionGroupID,
          ...item,
          isRequired: requiredRadio,
          minSelect: item.minSelect || (requiredRadio ? 1 : 0),
          maxSelect: item.maxSelect || (requiredRadio ? 1 : 0),
          children: requiredRadio
            ? item.children.map((v) => {
                return { ...v, maxSelect: 0 };
              })
            : item.children,
        },
      ],
    };

    try {
      const res = await CallPutOptGroup(storeId, optionGroupID, param);
      if (res.result.code === 1) {
        queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
        window.alert(`${selectType ? '선택 가능 옵션 수량 설정이 완료되었습니다.' : '옵션 추가가 완료되었습니다.'}`);
        window.history.back();
      } else {
        window.alert(res.result.message);
      }
    } catch (error) {
      console.log('error', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectType || options) {
      putOtpGroup();
      return;
    }
    if (step === 4) {
      postAddOtpGroup();
    } else {
      setStep(step + 1);
    }
  };

  const rangeItem = () => {
    const defaultVal = requiredRadio ? '1' : '0';
    return (
      <>
        <div className="opt-range show">
          최소
          <label className="tf-won" htmlFor="minSelect">
            <input
              type="tel"
              name="minSelect"
              id="minSelect"
              className="tf-cont"
              maxLength={2}
              value={item.minSelect}
              placeholder={defaultVal}
              onChange={handleInputChange}
              disabled={permissionDisabled}
            />
            <span className="txt-measure">개</span>
          </label>
          최대
          <label className="tf-won" htmlFor="maxSelect">
            <input
              type="tel"
              name="maxSelect"
              id="maxSelect"
              className="tf-cont"
              maxLength={2}
              value={item.maxSelect}
              placeholder={defaultVal}
              onChange={handleInputChange}
              disabled={permissionDisabled}
            />
            <span className="txt-measure">개</span>
          </label>
        </div>

        {requiredRadio ? (
          <p className="desc-option show">
            <span className="red">최소 개수와 최대 개수를 입력하지 않은 경우 각각 “1개”로 자동 저장됩니다.</span>
            <span>최소 개수와 최대 개수는 각각 “1개” 이상으로 설정해야 합니다. (“0개” 설정불가)</span>
            <span>
              최소 개수와 최대 개수를 동일한 개수로 설정한 경우 해당 개수만큼 주문 시 옵션을 필수적으로 선택해야 합니다.
              <br />
              ex: 최소 개수와 최대 개수를 모두 “2개”로 설정한 경우 주문 시 2개의 옵션을 필수적으로 선택해야 함
            </span>
            <span>해당 옵션을 선택할 경우 하위 옵션 별 선택 가능 수량을 설정할 수 없습니다.</span>
          </p>
        ) : (
          <p className="desc-option show">
            <span className="red">최소 개수와 최대 개수를 입력하지 않은 경우 각각 “0개”로 자동 저장됩니다.</span>
            <span>
              최소 개수와 최대 개수를 모두 “0개”로 설정한 경우 주문 시 옵션을 최소 0개, 최대 99개까지 추가로 선택할 수
              있습니다.
            </span>
            <span>
              최소 개수와 최대 개수를 동일한 개수로 설정 시 해당 개수만큼 주문 시 옵션을 추가적으로 선택할 수 있습니다.
              <br />
              ex: 최소 개수와 최대 개수를 모두 “10개”로 설정한 경우 주문 시 10개의 옵션을 추가적으로 선택할 수 있음
            </span>
            <span>해당 옵션을 선택할 경우 하위 옵션 별 선택 가능 수량을 설정할 수 있습니다.</span>
          </p>
        )}
      </>
    );
  };

  const showRange = (show: boolean) => {
    if (show) {
      if (requiredRadio) return rangeItem();
    } else if (!show && requiredRadio === 0) return rangeItem();
    return null;
  };

  const removeGroup = (num: number) => {
    const child = item.children;
    const newArr = child.filter((v: any, index: number) => index !== num);
    setItem((prev) => {
      return {
        ...prev,
        children: newArr,
      };
    });
  };

  const addGroup = () => {
    setIsDirty(true);
    setItem((prev) => {
      return {
        ...prev,
        children: prev.children.concat({
          isDisplay: 1,
          maxSelect: 0,
          name: '',
          price: '',
          alcohol: 0,
        }),
      };
    });
  };

  const initLoading = async () => {
    setLoading(true);
    try {
      const res = await CallGetOptionGroup(storeId);
      if (res.result.code === 1) {
        const current = res.data.find((v) => v.ID === Number(optionGroupID)) as any;
        setItem(current);
        if (options) {
          setRequiredRadio(current.isRequired);
        }
      }
    } catch (error) {
      console.log('error', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectType) {
      setRequiredRadio(Number(selectType));
      setStep(2);
      initLoading();
    }
  }, [selectType]);

  useEffect(() => {
    if (options) {
      setStep(3);
      initLoading();
    }
  }, [options]);

  useEffect(() => {
    const { minSelect, maxSelect, require } = prevItem;
    setItem((prev) => {
      if (require !== requiredRadio) {
        return { ...prev, minSelect, maxSelect };
      }
      return { ...prev, minSelect: '', maxSelect: '' };
    });
    prevItem = { require: requiredRadio, minSelect: item.minSelect, maxSelect: item.maxSelect };
  }, [requiredRadio]);

  useEffect(() => {
    return () => {
      prevItem = {
        require: null,
        maxSelect: '',
        minSelect: '',
      };
    };
  }, []);

  return (
    <article id="mArticle" className="art-product menu-sortable-list opt-add">
      {!selectType && !options && (
        <span className="opt-step">
          <em>{step}</em> / 4
        </span>
      )}
      {step === 1 && (
        <div className="view-sort">
          <div className="box-product">
            <strong className="lab-product">
              옵션 그룹명<span className="mark-ess">*</span>
            </strong>
            <input
              type="text"
              id="title"
              name="title"
              className="tf-cont"
              placeholder="예)사리추가"
              autoComplete="off"
              value={item.title}
              onChange={handleInputChange}
              maxLength={255}
              disabled={permissionDisabled}
            />
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="view-sort">
          <div className="box-product">
            <strong className="lab-product">
              주문 시 옵션 선택 여부<span className="mark-ess">*</span>
            </strong>
            <CommonRadioButton
              name="Required"
              id="Required"
              value={1}
              labelHtml="주문 시 옵션을 <em>필수적</em>으로 선택해야 합니다."
              check={requiredRadio === 1}
              onChangeHandler={(e) => {
                setIsDirty(true);
                setRequiredRadio(1);
              }}
            />
            {showRange(true)}
            <CommonRadioButton
              name="notRequired"
              id="notRequired"
              value={0}
              label="주문 시 옵션을 추가적으로 선택할 수 있습니다.​"
              check={requiredRadio === 0}
              onChangeHandler={(e) => {
                setIsDirty(true);
                setRequiredRadio(0);
              }}
            />
            {showRange(false)}
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="view-sort">
          <div className="box-product">
            <strong className="lab-product">
              옵션명<span className="mark-ess">*</span>
            </strong>
            {requiredRadio === 1 && (
              <p className="desc-option-red">
                * 옵션 그룹에 설정한 최소 / 최대 개수에 따라 주문 시 하위 옵션을 필수적으로 선택해야 합니다.
              </p>
            )}
            {requiredRadio === 0 && (
              <p className="desc-option-red">
                * 하위 옵션의 최대 선택 가능 개수를 설정하지 않은 경우, 옵션 그룹에 설정한 최소 / 최대 개수에 따라 주문
                시 하위 옵션을 선택할 수 있습니다.​
                <br />* 옵션 그룹의 최소 / 최대 개수와 하위 옵션의 최대 선택 가능 개수를 모두 설정하지 않은 경우, 주문
                시 하위 옵션의 수량은 옵션 별로 최대 99개까지 추가할 수 있습니다.
              </p>
            )}

            <ul className="list-price">
              {item.children.map((v, idx) => {
                return (
                  <li key={`childInput-${idx}`}>
                    <input
                      type="text"
                      name="name"
                      className="tf-cont"
                      placeholder="예)라면사리"
                      autoComplete="off"
                      value={item.children[idx].name}
                      data-string="text"
                      onChange={(e) => {
                        setIsDirty(true);
                        const val = e.target.value;
                        const child = item.children;
                        child[idx].name = val.slice(0, 255);
                        setItem((prev) => {
                          return {
                            ...prev,
                            children: child,
                          };
                        });
                      }}
                      maxLength={255}
                      disabled={permissionDisabled}
                    />
                    <label className="tf-won" htmlFor="price">
                      <input
                        type="tel"
                        name="price"
                        className="tf-cont"
                        max="100000000"
                        placeholder="0"
                        data-pattern="price"
                        value={item.children[idx].price}
                        onChange={(e) => {
                          setIsDirty(true);
                          const val = e.target.value;
                          if (/\D/.test(val)) {
                            return;
                          }
                          const child = item.children;
                          child[idx].price = val;
                          setItem((prev) => {
                            return {
                              ...prev,
                              children: child,
                            };
                          });
                        }}
                        disabled={permissionDisabled}
                      />
                      <span className="txt-measure">원</span>
                    </label>
                    {requiredRadio === 0 && (
                      <label className="tf-won label" htmlFor="maxSelect">
                        <span className="txt-measure pre">최대</span>
                        <input
                          type="tel"
                          name="maxSelect"
                          className="tf-cont"
                          maxLength={2}
                          placeholder="1"
                          value={item.children[idx].maxSelect}
                          onChange={(e) => {
                            setIsDirty(true);
                            const val = e.target.value;
                            if (/\D/.test(val)) {
                              return;
                            }
                            const child = item.children;
                            child[idx].maxSelect = Number(val);
                            setItem((prev) => {
                              return {
                                ...prev,
                                children: child,
                              };
                            });
                          }}
                          disabled={permissionDisabled}
                        />
                        <span className="txt-measure">개</span>
                      </label>
                    )}
                    {item.children.length > 1 && (
                      <button
                        type="button"
                        className="btn-del"
                        onClick={() => {
                          removeGroup(idx);
                        }}
                      >
                        삭제
                      </button>
                    )}
                    <div className="check">
                      <span className="alcohol-label">주류메뉴</span>
                      <CommonCheckBox
                        id={`alcohol-${idx}`}
                        onChange={() => {
                          setIsDirty(true);
                          const child = item.children;
                          child[idx].alcohol = child[idx].alcohol === 0 ? 1 : 0;
                          setItem((prev) => {
                            return {
                              ...prev,
                              children: child,
                            };
                          });
                        }}
                        value={Boolean(item.children[idx].alcohol)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
            <button type="button" className="btn-round-add" onClick={addGroup}>
              <span className="ico-plus" />
              추가
            </button>
          </div>
        </div>
      )}
      {step === 4 && (
        <div className="view-sort">
          <div className="box-product">
            <strong className="lab-product">옵션 그룹 확인</strong>
            <p className="desc-product">
              등록한 옵션 그룹 정보가 맞는지 확인 후 저장 버튼을 클릭해 주시면 내 매장에 옵션 그룹이 등록됩니다.
            </p>
            <div className="list-product-option">
              <dl className="list-option">
                <dt>옵션 그룹명</dt>
                <dd>{item.title}</dd>
              </dl>
              <dl className="list-option">
                <dt>가격 설정</dt>
                <dd>
                  {item.children.map((v, idx) => {
                    return (
                      <div key={`childShow-${idx}`}>
                        {v.name} {v.alcohol === 1 && <span className="alcohol-badge option">19</span>} :{' '}
                        {Number(v.price).toLocaleString()}원
                      </div>
                    );
                  })}
                </dd>
              </dl>
              <dl className="list-option">
                <dt>
                  주문 시 옵션
                  <br />
                  선택 여부
                </dt>
                {requiredRadio === 1 && (
                  <dd>
                    주문 시 옵션을 <span>필수적</span>으로 선택해야 합니다.
                  </dd>
                )}
                {requiredRadio === 0 && <dd>주문 시 옵션을 추가적으로 선택할 수 있습니다.</dd>}
              </dl>
            </div>
          </div>
        </div>
      )}
      <div className="fixedBtnGroup">
        {step > 1 && !selectType && !options && (
          <button type="button" className="btnEdit" onClick={goPrevStep}>
            이전
          </button>
        )}
        <button
          type="button"
          id="btnSubmit"
          className="btnDone"
          disabled={handleBtnDisabled || !isDirty || permissionDisabled}
          onClick={handleConfirm}
        >
          {step === 4 || selectType || options ? '저장' : '다음'}
        </button>
      </div>
      <Spinner show={loading} />
    </article>
  );
};

export default OptionGroupAdd;
